import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getServicesQueryOptions } from '@/services/api';
import type { CdpOutResult } from '@/services/auth/types';
import { SHARED_QUERY_OPTIONS } from './constants';
import { mapCdpOutToSearchResult, matchesCdpOut } from './utils/integration-results';
import type { SearchSource } from './utils/source-bookkeeping';

type UseCdpOutSourceOptions = {
  workspaceId: number;
  enabled: boolean;
  query: string;
};

type CdpOutSource = SearchSource<CdpOutResult>;

export function useCdpOutSource({ workspaceId, enabled, query }: UseCdpOutSourceOptions): CdpOutSource {
  const cdpOutQuery = useQuery(
    getServicesQueryOptions(
      'getCdpOut',
      { params: { environment_id: workspaceId } },
      {
        enabled,
        ...SHARED_QUERY_OPTIONS,
        // 404 means no CDP Out row — treat as empty, not an error.
        throwOnError: false,
        retry: false,
        select: (data) => data.cdp_out,
      },
    ),
  );

  const results = useMemo(() => {
    const row = cdpOutQuery.data;
    if (row == null || !matchesCdpOut(query)) return [];
    return [mapCdpOutToSearchResult(row, workspaceId)];
  }, [cdpOutQuery.data, query, workspaceId]);

  return { ...cdpOutQuery, results };
}
